// src/components/Layout/StickyHeader.js
import React, { useState, useRef, useEffect } from 'react';
import styles from './StickyHeader.module.css';
import NavigationTooltip from '../NavigationTooltip';
import TooltipPortal from '../../utils/TooltipPortal';

const StickyHeader = ({
  day,
  currentTimeString,
  isTimeWarning,
  gold,
  location,
  onRest, // This will now be handleManualRest from Layout
  onTogglePause,
  onTravelTo, // New prop for travel functionality
  canRest,
  canTravel, // New prop to check if travel is allowed
  isPaused,
  // Mobile menu props
  isMobileMenuVisible,
  onToggleMobileMenu,
  // Settings props
  tooltipsEnabled = true
}) => {
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const locationName = location.charAt(0).toUpperCase() + location.slice(1);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the dropdown trigger and the portal-rendered dropdown menu
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Check if the click target is within the portal-rendered dropdown
        const portalRoot = document.getElementById('tooltip-root');
        if (portalRoot && portalRoot.contains(event.target)) {
          // Click is within the dropdown menu, don't close
          return;
        }
        setIsLocationDropdownOpen(false);
      }
    };

    if (isLocationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isLocationDropdownOpen]);

  // Get available travel destinations based on current location
  const getAvailableDestinations = () => {
    const destinations = [];
    
    if (location === 'home') {
      destinations.push({
        id: 'wilderness',
        name: 'Wilderness',
        icon: '/img/icons/wildIcon.png',
        actionKey: 'goToWilderness',
        energyCost: 5,
        timeCost: 60
      });
      destinations.push({
        id: 'shop',
        name: 'Shop',
        icon: '/img/icons/innIcon.png',
        actionKey: 'goToShop',
        energyCost: 0,
        timeCost: 20
      });
    } else if (location === 'wilderness') {
      destinations.push({
        id: 'home',
        name: 'Home',
        icon: '/img/icons/homeIcon.png',
        actionKey: 'returnHome',
        energyCost: 5,
        timeCost: 60
      });
    } else if (location === 'shop') {
      destinations.push({
        id: 'home',
        name: 'Home',
        icon: '/img/icons/homeIcon.png',
        actionKey: 'leaveShop',
        energyCost: 0,
        timeCost: 20
      });
    }
    
    return destinations;
  };

  const handleTravelSelect = (destination) => {
    if (canTravel && onTravelTo) {
      onTravelTo(destination.id, destination.actionKey);
    }
    setIsLocationDropdownOpen(false);
  };

  const availableDestinations = getAvailableDestinations();

  return (
    <div className={styles.stickyHeaderContainer}>
      {/* Mobile Menu Toggle Button - Only visible on mobile */}
      {onToggleMobileMenu && (
        <NavigationTooltip text={isMobileMenuVisible ? "Close Menu - Hide the navigation menu" : "Open Menu - Show the navigation menu with tabs and options"} disabled={!tooltipsEnabled}>
          <button 
            className={styles.mobileMenuToggle}
            onClick={onToggleMobileMenu}
            aria-label="Toggle Navigation Menu"
          >
            {isMobileMenuVisible ? '✕' : '☰'}
          </button>
        </NavigationTooltip>
      )}
      
      <NavigationTooltip text={`Current Day: ${day} - Your progress through the adventure. Each day has 21 game hours (~21 real minutes) from 4:00 AM to 1:00 AM.`} disabled={!tooltipsEnabled}>
        <div className={styles.headerItem}>
          <img src="/img/icons/dayIcon.png" alt="Day" className={styles.headerIcon} />
          <span className={styles.headerText}>Day {day}</span>
        </div>
      </NavigationTooltip>

      <NavigationTooltip text={canRest ? "Rest (End Day) - Click to end the current day and rest. Time will advance to the next morning." : (location !== 'home' ? "Return home to rest - You must be at home to rest and end the day." : "Cannot rest now - You may be in combat or another activity.")} disabled={!tooltipsEnabled}>
        <button 
          onClick={onRest} // onRest now triggers the EndOfDayModal via handleManualRest
          disabled={!canRest} 
          className={styles.headerButton}
        >
          <img src="/img/icons/restIcon.png" alt="Rest" className={`${styles.headerIcon} ${!canRest ? styles.disabledIcon : ''}`} />
        </button>
      </NavigationTooltip>

      <NavigationTooltip text={isPaused ? "Resume Game - Click to resume time and continue playing." : "Pause Game - Click to pause time and open the menu. Time stops while paused."} disabled={!tooltipsEnabled}>
        <button 
          onClick={onTogglePause}
          className={`${styles.headerButton} ${isPaused ? styles.pausedButton : ''}`}
        >
          <img 
            src={isPaused ? "/img/icons/play_button.png" : "/img/icons/pause_button.png"} 
            alt={isPaused ? "Play" : "Pause"} 
            className={styles.headerIcon}
          />
        </button>
      </NavigationTooltip>



      <NavigationTooltip text={`Current Time: ${currentTimeString} - Each day runs from 4:00 AM to 1:00 AM the next day (21 hours, ~21 real minutes). ${isTimeWarning ? "Time is running low! Consider resting soon." : "You have plenty of time left in the day."}`} disabled={!tooltipsEnabled}>
        <div className={`${styles.headerItem} ${isTimeWarning ? styles.timeWarning : ''}`}>
          <img src="/img/icons/timeIcon.png" alt="Time" className={styles.headerIcon} />
          <span className={styles.headerText}>{currentTimeString}</span>
        </div>
      </NavigationTooltip>

      <NavigationTooltip text={`Gold: ${gold} G - Your currency for purchasing items, equipment, and buildings. Earn gold by completing quests, selling items, or crafting.`} disabled={!tooltipsEnabled}>
        <div className={styles.headerItem}>
          <img src="/img/icons/goldIcon.png" alt="Gold" className={styles.headerIcon} />
          <span className={styles.headerText}>{gold} G</span>
        </div>
      </NavigationTooltip>

      {/* Location Dropdown */}
      <div className={styles.locationDropdownContainer} ref={dropdownRef}>
        <NavigationTooltip text={canTravel ? `Current Location: ${locationName} - Click to travel to another location. Travel costs energy and time.` : `Current Location: ${locationName} - Cannot travel now. You may be in combat or another activity that prevents travel.`} disabled={!tooltipsEnabled}>
          <button 
            className={`${styles.headerItem} ${styles.locationDropdown} ${!canTravel ? styles.disabled : ''}`}
            onClick={() => canTravel && setIsLocationDropdownOpen(!isLocationDropdownOpen)}
          >
            <img src="/img/icons/locationIcon.png" alt="Location" className={styles.headerIcon} />
            <span className={styles.headerText}>{locationName}</span>
            {availableDestinations.length > 0 && canTravel && (
              <span className={`${styles.dropdownArrow} ${isLocationDropdownOpen ? styles.open : ''}`}>
                &#9660;
              </span>
            )}
          </button>
        </NavigationTooltip>

        <TooltipPortal isVisible={isLocationDropdownOpen && availableDestinations.length > 0}>
          <div 
            className={styles.locationDropdownMenu}
            style={{
              position: 'fixed',
              top: dropdownRef.current ? `${dropdownRef.current.getBoundingClientRect().bottom + 8}px` : '60px',
              right: dropdownRef.current ? `${Math.max(10, window.innerWidth - dropdownRef.current.getBoundingClientRect().right)}px` : '10px',
              pointerEvents: 'auto', // Allow interaction with the dropdown
            }}
          >
            <div className={styles.dropdownHeader}>Travel to:</div>
            {availableDestinations.map((destination) => (
              <button
                key={destination.id}
                className={styles.dropdownOption}
                onClick={() => handleTravelSelect(destination)}
                title={`${destination.name} (${destination.energyCost} energy, ${destination.timeCost} min)`}
              >
                <img 
                  src={destination.icon} 
                  alt={destination.name} 
                  className={styles.destinationIcon} 
                />
                <div className={styles.destinationInfo}>
                  <span className={styles.destinationName}>{destination.name}</span>
                  <span className={styles.destinationCost}>
                    {destination.energyCost}E • {destination.timeCost}min
                  </span>
                </div>
              </button>
            ))}
          </div>
        </TooltipPortal>
      </div>
    </div>
  );
};

export default StickyHeader;